// The police vehicles that leave (docs/plans/2026-10-17-asset-phases.md, phase 4): the surveillance van drives out
// through the rings when the march starts (chalo.json march.vanLeaves: through I2, M3 and O4), and the detention bus
// drives off west with each load. Client-side animation only, drawn top-down from the fleet atlas like the traffic.
// While one is away, vanGone / busGone tell the Chalo world to hide its parked sprite.
import type { GameMap } from '../shared/map';
import type { RingsJson } from '../shared/chalo';
import FLEET_ATLAS from './fleet.json';
import { CAR_SCALE, PACE } from './traffic';

type XY = [number, number];
type Rect = { x: number; y: number; w: number; h: number };
const sprites = FLEET_ATLAS.sprites as Record<string, Rect>;
export const VAN = 'surveillance_van', BUS = 'quaternius-public-transport/Bus';

/** Driving: top speed (m/s, before PACE, as the traffic's classes), how hard it pulls away, how far past its last point. */
const VAN_MPS = 9, BUS_MPS = 8, ACCEL = 1.6 * PACE, EXIT_M = { van: 140, bus: 90 }, FADE_M = 40;

/** The checkpoint ids named in march.vanLeaves ("through I2, M3 and O4 …"), in order. */
export function vanCheckpoints(vanLeaves: string | undefined): string[] {
  return vanLeaves?.match(/\b[IMO]\d\b/g) ?? ['I2', 'M3', 'O4'];
}

/** A polyline (tiles) carried on `exitM` metres past its end, straight on, so a vehicle drives off rather than stopping. */
function extend(pts: XY[], exitM: number, tileM: number): XY[] {
  const [a, b] = pts.slice(-2), d = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1, k = exitM / tileM / d;
  return [...pts, [b[0] + (b[0] - a[0]) * k, b[1] + (b[1] - a[1]) * k]];
}

/** The van's way out, in tiles: from where it parks, through the named checkpoints' gaps, then on off the map. */
export function vanRoute(R: RingsJson, vanLeaves?: string): XY[] {
  const at = new Map(R.checkpoints.map((c) => [c.id, [c.x, c.y] as XY]));
  const via = vanCheckpoints(vanLeaves).map((id) => at.get(id)).filter((p): p is XY => !!p);
  return extend([[R.van.x, R.van.y], ...via], EXIT_M.van, R.tileM);
}

/** The bus's way off, in tiles: from its stand, due west (the parked bus faces west), off the map's edge. */
export function busRoute(R: RingsJson): XY[] {
  return extend([[R.bus.x, R.bus.y], [R.bus.x - 1, R.bus.y]], EXIT_M.bus, R.tileM);
}

/** A route in world px, walked by distance. */
export class Route {
  readonly pts: number[];
  readonly cum: number[] = [0];
  constructor(tiles: XY[], tile: number) {
    this.pts = tiles.flatMap(([x, y]) => [x * tile, y * tile]);
    for (let i = 1; i < tiles.length; i++) this.cum.push(this.cum[i - 1] + Math.hypot(this.pts[2 * i] - this.pts[2 * i - 2], this.pts[2 * i + 1] - this.pts[2 * i - 1]));
  }
  get length() { return this.cum[this.cum.length - 1]; }
  /** position and heading (radians, 0 east, y down) at distance s */
  at(s: number): { x: number; y: number; h: number } {
    const c = this.cum, n = c.length;
    let i = 1;
    while (i < n - 1 && c[i] < s) i++;
    const ax = this.pts[2 * i - 2], ay = this.pts[2 * i - 1], bx = this.pts[2 * i], by = this.pts[2 * i + 1];
    const seg = c[i] - c[i - 1] || 1, k = Math.max(0, Math.min(1, (s - c[i - 1]) / seg));
    return { x: ax + (bx - ax) * k, y: ay + (by - ay) * k, h: Math.atan2(by - ay, bx - ax) };
  }
}

/** One vehicle on its way out. */
export class Drive {
  s = 0; v = 0; rh: number; done = false;
  constructor(readonly key: string, readonly route: Route, private vmax: number, private accel: number, private fade: number) {
    this.rh = route.at(0).h;
  }
  step(dt: number) {
    if (this.done) return;
    this.v = Math.min(this.vmax, this.v + this.accel * dt);
    this.s += this.v * dt;
    if (this.s >= this.route.length) { this.s = this.route.length; this.done = true; }
    // the heading eases round the bends, as the traffic's does
    let dh = this.route.at(this.s).h - this.rh; dh -= Math.round(dh / (2 * Math.PI)) * 2 * Math.PI;
    this.rh += dh * Math.min(1, dt * 5);
  }
  get pos() { return this.route.at(this.s); }
  /** fades over the route's last stretch, off the map's edge */
  get alpha() { return this.done ? 0 : Math.max(0, Math.min(1, (this.route.length - this.s) / this.fade)); }
}

export class Convoy {
  /** the van has driven off (or is driving): hide the parked van */
  vanGone = false;
  /** a loaded bus is driving off: hide the parked bus until it is gone (the next, empty, bus stands in its place) */
  busGone = false;
  van: Drive | null = null;
  buses: Drive[] = [];
  private img: HTMLImageElement | null = null;
  private readonly metre: number;
  private readonly vanWay: Route; private readonly busWay: Route;

  constructor(m: GameMap, R: RingsJson, vanLeaves?: string) {
    this.metre = m.tile / (m.mpt || 1);
    this.vanWay = new Route(vanRoute(R, vanLeaves), m.tile);
    this.busWay = new Route(busRoute(R), m.tile);
    if (typeof Image !== 'undefined') {
      const i = new Image();
      i.onload = () => { this.img = i; };
      i.src = FLEET_ATLAS.image;
    }
  }

  /** The march starts (or the round is won): the van drives out through the rings. Once a round. */
  vanLeaves() {
    if (this.vanGone) return;
    this.vanGone = true;
    this.van = new Drive(VAN, this.vanWay, VAN_MPS * PACE * this.metre, ACCEL * this.metre, FADE_M * this.metre);
  }

  /** Hook for detention (not built yet): a loaded bus drives off west. */
  busLeaves() {
    this.busGone = true;
    this.buses.push(new Drive(BUS, this.busWay, BUS_MPS * PACE * this.metre, ACCEL * 0.7 * this.metre, FADE_M * this.metre));
  }

  /** A new round: the van and bus are back where they park. */
  reset() { this.vanGone = this.busGone = false; this.van = null; this.buses = []; }

  update(dt: number) {
    dt = Math.min(dt, 0.1);
    this.van?.step(dt);
    for (const b of this.buses) b.step(dt);
    this.buses = this.buses.filter((b) => !b.done);
    this.busGone = this.buses.length > 0;
  }

  /** Draw in world coordinates (the caller has applied the camera), on the ground under the people, as the traffic. */
  draw(g: CanvasRenderingContext2D, vx0: number, vy0: number, vw: number, vh: number) {
    if (!this.img) return;
    const pad = 30 * this.metre;
    for (const d of [...(this.van && !this.van.done ? [this.van] : []), ...this.buses]) {
      const p = d.pos, a = sprites[d.key];
      if (!a || p.x < vx0 - pad || p.x > vx0 + vw + pad || p.y < vy0 - pad || p.y > vy0 + vh + pad) continue;
      const k = (this.metre / FLEET_ATLAS.ppm) * CAR_SCALE, w = a.w * k, h = a.h * k;
      g.save(); // the shadow, cast east and a little south by the dusk sun, as the traffic's
      g.translate(p.x + 0.45 * this.metre, p.y + 0.2 * this.metre); g.rotate(d.rh + Math.PI / 2);
      g.globalAlpha = 0.3 * d.alpha; g.fillStyle = '#080A10';
      g.fillRect(-w * 0.45, -h * 0.47, w * 0.9, h * 0.94);
      g.restore();
      g.save();
      g.translate(p.x, p.y); g.rotate(d.rh + Math.PI / 2); // sprites face up the screen
      g.globalAlpha = d.alpha;
      g.drawImage(this.img, a.x, a.y, a.w, a.h, -w / 2, -h / 2, w, h);
      g.restore();
    }
  }
}
